import React, { useState } from 'react';
import { 
  Settings, 
  Building2, 
  Users, 
  Shield, 
  Globe, 
  CreditCard,
  Bell,
  Palette,
  Database,
  ChevronRight,
  ChevronDown,
  Building,
  Flag,
  Percent,
  ToggleLeft,
  MapPin,
  Landmark,
  Crown,
  Store
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { 
  franchiseOwner, 
  franchisees, 
  brands, 
  countries, 
  vatConfig,
  formatCurrency 
} from '@/config';
import { outlets } from '@/config/outlets';

// Feature toggles
const featureToggles = [
  { id: 'pos', name: 'POS Module', description: 'Point of sale operations', enabled: true },
  { id: 'inventory', name: 'Inventory Management', description: 'Stock and recipe management', enabled: true },
  { id: 'finance', name: 'Finance & Reporting', description: 'Financial reports and budgets', enabled: true },
  { id: 'hr', name: 'HR & Scheduling', description: 'Staff management and shifts', enabled: true },
  { id: 'reservations', name: 'Reservations', description: 'Table booking system', enabled: true },
  { id: 'loyalty', name: 'Loyalty Program', description: 'Customer rewards and points', enabled: false },
  { id: 'delivery', name: 'Delivery Integration', description: 'Wolt, Foodora integration', enabled: true },
  { id: 'analytics', name: 'Advanced Analytics', description: 'AI-powered insights', enabled: false },
  { id: 'multiCurrency', name: 'Multi-Currency', description: 'Support for SEK, EUR, USD', enabled: true },
];

export function AdminConfig() {
  const [activeTab, setActiveTab] = useState('franchise');

  const getCountryFlag = (code) => {
    const flags = { NO: '🇳🇴', SE: '🇸🇪', DK: '🇩🇰', GB: '🇬🇧' };
    return flags[code] || '🏳️';
  };

  return (
    <div data-testid="admin-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl text-brand-charcoal">Admin & Configuration</h1>
        <p className="text-gray-500 text-sm mt-1">Manage system settings, franchise structure, and configurations</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="franchise" className="gap-2">
            <Crown className="w-4 h-4" />
            <span className="hidden sm:inline">Franchise</span>
          </TabsTrigger>
          <TabsTrigger value="outlets" className="gap-2">
            <Store className="w-4 h-4" />
            <span className="hidden sm:inline">Outlets</span>
          </TabsTrigger>
          <TabsTrigger value="countries" className="gap-2">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Countries</span>
          </TabsTrigger>
          <TabsTrigger value="vat" className="gap-2">
            <Percent className="w-4 h-4" />
            <span className="hidden sm:inline">VAT</span>
          </TabsTrigger>
          <TabsTrigger value="brands" className="gap-2">
            <Palette className="w-4 h-4" />
            <span className="hidden sm:inline">Brands</span>
          </TabsTrigger>
          <TabsTrigger value="features" className="gap-2">
            <ToggleLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Features</span>
          </TabsTrigger>
        </TabsList>

        {/* FRANCHISE TAB */}
        <TabsContent value="franchise" className="mt-6 space-y-6">
          {/* Franchise Owner Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-orange/10 rounded-lg">
                  <Crown className="w-5 h-5 text-brand-orange" />
                </div>
                <div>
                  <CardTitle>Franchise Owner (IP Holder)</CardTitle>
                  <CardDescription>Master franchise entity and brand owner</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Legal Name</p>
                    <p className="font-semibold">{franchiseOwner.legalName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Registration Number</p>
                    <p className="font-mono">{franchiseOwner.registrationNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Headquarters</p>
                    <p>{franchiseOwner.headquarters.address}, {franchiseOwner.headquarters.city}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Founder</p>
                    <p>{franchiseOwner.founder}</p>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <p className="font-semibold text-sm text-gray-700">Franchise Configuration</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">Royalty Fee</p>
                      <p className="font-mono">{franchiseOwner.franchiseConfig.royaltyPercentage}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Marketing Fee</p>
                      <p className="font-mono">{franchiseOwner.franchiseConfig.marketingFeePercentage}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Initial Fee</p>
                      <p className="font-mono">{formatCurrency(franchiseOwner.franchiseConfig.initialFranchiseFee, 'NOK')}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Contract Term</p>
                      <p className="font-mono">{franchiseOwner.franchiseConfig.contractTermYears} years</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Franchisees */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-spice-cardamom/10 rounded-lg">
                    <Building className="w-5 h-5 text-spice-cardamom" />
                  </div>
                  <div>
                    <CardTitle>Franchisees</CardTitle>
                    <CardDescription>Corporate and franchise-operated companies</CardDescription>
                  </div>
                </div>
                <Button className="bg-brand-orange hover:bg-brand-orange/90">
                  Add Franchisee
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Contract</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {franchisees.map((franchisee) => (
                    <TableRow key={franchisee.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{franchisee.name}</p>
                          <p className="text-xs text-gray-500">{franchisee.legalName}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={franchisee.type === 'corporate' ? 'default' : 'secondary'}>
                          {franchisee.type === 'corporate' ? 'Corporate' : 'Franchise'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          <span>{getCountryFlag(franchisee.country)}</span>
                          {franchisee.country}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{franchisee.contactPerson}</p>
                          <p className="text-xs text-gray-500">{franchisee.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{franchisee.contractStartDate}</p>
                          {franchisee.contractEndDate && (
                            <p className="text-xs text-gray-500">→ {franchisee.contractEndDate}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-spice-cardamom">{franchisee.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OUTLETS TAB */}
        <TabsContent value="outlets" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-orange/10 rounded-lg">
                    <Store className="w-5 h-5 text-brand-orange" />
                  </div>
                  <div>
                    <CardTitle>Outlets</CardTitle>
                    <CardDescription>{outlets.length} locations across {Object.keys(countries).filter(c => countries[c].isActive).length} countries</CardDescription>
                  </div>
                </div>
                <Button className="bg-brand-orange hover:bg-brand-orange/90">
                  Add Outlet
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Ownership</TableHead>
                    <TableHead>Franchisee</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outlets.map((outlet) => (
                    <TableRow key={outlet.id}>
                      <TableCell className="font-mono text-sm">{outlet.code}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{getCountryFlag(outlet.address.country)}</span>
                          <span className="font-medium">{outlet.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{outlet.address.street}</p>
                          <p className="text-gray-500">{outlet.address.city}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={outlet.ownershipType === 'corporate' ? 'default' : 'outline'}>
                          {outlet.ownershipType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {franchisees.find(f => f.id === outlet.franchiseeId)?.name || '-'}
                      </TableCell>
                      <TableCell className="font-mono">{outlet.currency}</TableCell>
                      <TableCell>
                        <Badge className={outlet.status === 'active' ? 'bg-spice-cardamom' : ''}>
                          {outlet.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COUNTRIES TAB */}
        <TabsContent value="countries" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-orange/10 rounded-lg">
                  <Globe className="w-5 h-5 text-brand-orange" />
                </div>
                <div>
                  <CardTitle>Countries & Regions</CardTitle>
                  <CardDescription>Regional settings, currencies, and localization</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>VAT Label</TableHead>
                    <TableHead>Timezone</TableHead>
                    <TableHead>Locale</TableHead>
                    <TableHead>Primary</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.values(countries).map((country) => (
                    <TableRow key={country.code} className={!country.isActive ? 'opacity-50' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{getCountryFlag(country.code)}</span>
                          <div>
                            <p className="font-medium">{country.name}</p>
                            <p className="text-xs text-gray-500">{country.nameLocal}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {country.currency} ({country.currencySymbol})
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{country.vatLabel}</TableCell>
                      <TableCell className="text-sm">{country.timezone}</TableCell>
                      <TableCell className="font-mono text-sm">{country.locale}</TableCell>
                      <TableCell>
                        {country.isPrimary && <Badge className="bg-brand-orange">Primary</Badge>}
                      </TableCell>
                      <TableCell>
                        <Switch checked={country.isActive} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VAT TAB */}
        <TabsContent value="vat" className="mt-6 space-y-6">
          {Object.entries(vatConfig).map(([countryCode, config]) => (
            <Card key={countryCode}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getCountryFlag(countryCode)}</span>
                  <div>
                    <CardTitle>{countries[countryCode]?.name} - {config.vatLabel}</CardTitle>
                    <CardDescription>VAT rates by order type and category</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order Type</TableHead>
                      <TableHead className="text-right">Food</TableHead>
                      <TableHead className="text-right">Beverages</TableHead>
                      <TableHead className="text-right">Alcohol</TableHead>
                      <TableHead className="text-right">Merchandise</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(config.rates).map(([orderType, rates]) => (
                      <TableRow key={orderType}>
                        <TableCell className="font-medium capitalize">{orderType.replace(/([A-Z])/g, ' $1')}</TableCell>
                        <TableCell className="text-right font-mono">
                          <Badge variant={rates.food === 0.25 ? 'default' : 'secondary'}>
                            {(rates.food * 100)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <Badge variant={rates.beverages === 0.25 ? 'default' : 'secondary'}>
                            {(rates.beverages * 100)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <Badge variant="destructive">
                            {(rates.alcohol * 100)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <Badge variant="default">
                            {(rates.merchandise * 100)}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                  <strong>Note:</strong> VAT rates are configurable per country, outlet, order type, and item category.
                  Default rate: {(config.defaultRate * 100)}%
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* BRANDS TAB */}
        <TabsContent value="brands" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {brands.map((brand) => (
              <Card key={brand.id} className={!brand.isActive ? 'opacity-50' : ''}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {brand.logo ? (
                      <img src={brand.logo} alt={brand.name} className="w-16 h-16 object-contain rounded-lg bg-brand-charcoal p-2" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg flex items-center justify-center" style={{ backgroundColor: brand.primaryColor }}>
                        <span className="text-white font-display text-xl">{brand.shortName}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{brand.name}</h3>
                        {!brand.isActive && <Badge variant="secondary">Inactive</Badge>}
                      </div>
                      <p className="text-sm text-gray-500 mb-3">{brand.description}</p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: brand.primaryColor }} />
                          <span className="text-xs font-mono">{brand.primaryColor}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: brand.secondaryColor }} />
                          <span className="text-xs font-mono">{brand.secondaryColor}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* FEATURES TAB */}
        <TabsContent value="features" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-orange/10 rounded-lg">
                  <ToggleLeft className="w-5 h-5 text-brand-orange" />
                </div>
                <div>
                  <CardTitle>Feature Toggles</CardTitle>
                  <CardDescription>Enable or disable system features globally or per outlet</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {featureToggles.map((feature) => (
                  <div 
                    key={feature.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-brand-charcoal">{feature.name}</p>
                      <p className="text-sm text-gray-500">{feature.description}</p>
                    </div>
                    <Switch checked={feature.enabled} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle>System Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Version</p>
              <p className="font-mono font-semibold">v1.0.0-beta</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Environment</p>
              <p className="font-mono font-semibold">Preview</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Base Currency</p>
              <p className="font-mono font-semibold">NOK</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Active Outlets</p>
              <p className="font-mono font-semibold">{outlets.filter(o => o.status === 'active').length}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">API Status</p>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-spice-cardamom rounded-full animate-pulse" />
                <span className="font-semibold text-spice-cardamom">Online</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
